import { useState } from "react";
import { GripVertical, ChevronDown, Trash2, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, Checkbox } from "@tellescope/ui";
import { Button, Label, Input } from "@tellescope/design-system";
import type { VisibilityCondition } from "@tellescope/visibility-schema";
import { SHOP_MAX_QUANTITY } from "@tellescope/shop-schema";
import { formatShopPrice } from "../enduser/formatShopPrice";
import { PriceVariantRow } from "./PriceVariantRow";
import { ProductVisibilitySection } from "./ProductVisibilitySection";
import type { SelectedShopEntry } from "./types";
import type { QuantitySupport } from "./useShopBlockEditorLogic";
import type { StripeShopProduct, StripeShopPrice } from "./stripeShopTypes";

interface ProductListItemProps {
  entry: SelectedShopEntry;
  isVariantResolved: (stripePriceId: string) => boolean;
  isDragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRemove: () => void;
  /** Live Stripe prices for this product — only populated while this row is the expanded one. */
  prices: StripeShopPrice[];
  pricesLoading: boolean;
  onTogglePrice: (product: StripeShopProduct, price: StripeShopPrice) => void;
  onBadgeChange: (badge: string | undefined) => void;
  onRecommendedChange: (recommended: boolean) => void;
  quantitySupport: QuantitySupport;
  /** Receives the input's raw text; the editor hook parses and clamps it. */
  onMaxQuantityChange: (inputValue: string) => void;
  onCategoryChange: (category: string | undefined) => void;
  onVisibilityChange: (condition: VisibilityCondition | undefined) => void;
}

/** Builds the minimal StripeShopProduct shape onTogglePrice needs to add/remove a variant from this entry. */
const toStripeShopProductStub = (entry: SelectedShopEntry): StripeShopProduct => ({
  id: entry.stripeProductId,
  active: true,
  name: entry.title,
  description: entry.description ?? null,
  images: entry.image ? [entry.image] : [],
  default_price: null,
});

export const ProductListItem = ({
  entry,
  isVariantResolved,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  isExpanded,
  onToggleExpanded,
  onRemove,
  prices,
  pricesLoading,
  onTogglePrice,
  onBadgeChange,
  onRecommendedChange,
  quantitySupport,
  onMaxQuantityChange,
  onCategoryChange,
  onVisibilityChange,
}: ProductListItemProps) => {
  const { t } = useTranslation("shop");
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const maxQuantityInputId = `shop-max-quantity-${entry.stripeProductId}`;
  const unresolvedVariants = entry.variants.filter((v) => !isVariantResolved(v.stripePriceId));
  const selectedPriceIds = new Set(entry.variants.map((v) => v.stripePriceId));

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn("rounded border bg-background transition-opacity", isDragging && "opacity-50")}
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 flex items-center gap-2 cursor-pointer min-w-0" onClick={onToggleExpanded}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{entry.title}</p>
            <p className="text-xs text-muted-foreground truncate">
              {entry.variants.map((variant) => formatShopPrice(t, variant)).join(" · ")}
            </p>
          </div>
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0",
              isExpanded && "rotate-180"
            )}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          hoverLabel={t("Remove from shop")}
          className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {isExpanded && (
        <div className="px-2 pb-3 pt-1 border-t space-y-3">
          {unresolvedVariants.length > 0 && (
            <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                {t(
                  "This product isn't configured in the web app yet — add it there first, then it will be available here."
                )}
              </span>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{t("Price Variants")}</Label>
            {pricesLoading ? (
              <p className="text-xs text-muted-foreground py-1">{t("Loading prices…")}</p>
            ) : prices.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1">{t("No active prices for this product.")}</p>
            ) : (
              <div className="rounded-md border">
                {prices.map((price) => (
                  <PriceVariantRow
                    key={price.id}
                    price={price}
                    selected={selectedPriceIds.has(price.id)}
                    onToggle={() => onTogglePrice(toStripeShopProductStub(entry), price)}
                  />
                ))}
              </div>
            )}
          </div>

          <Input
            value={entry.category ?? ""}
            onChange={(e) => onCategoryChange(e.target.value || undefined)}
            placeholder={t('Category (e.g. "Hair Loss")')}
            className="text-xs"
          />

          <Input
            value={entry.badge ?? ""}
            onChange={(e) => onBadgeChange(e.target.value || undefined)}
            placeholder={t('Badge text (e.g. "Popular"; defaults to "Recommended for you")')}
            className="text-xs"
          />

          <div className="space-y-1">
            <Label htmlFor={maxQuantityInputId} className="text-xs text-muted-foreground">
              {t("Maximum quantity per order")}
            </Label>
            <Input
              id={maxQuantityInputId}
              data-test="max-quantity-input"
              type="number"
              inputMode="numeric"
              min={1}
              max={SHOP_MAX_QUANTITY}
              step={1}
              placeholder="1"
              disabled={quantitySupport !== "allowed"}
              value={quantitySupport === "not-allowed" ? "" : entry.maxQuantity ?? ""}
              onChange={(e) => onMaxQuantityChange(e.target.value)}
              className="text-xs w-24"
            />
            {quantitySupport === "not-allowed" && (
              <p className="text-xs text-muted-foreground">
                {t("Only one-time products without add-on prices can be bought more than one at a time.")}
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <Checkbox
              checked={entry.recommended}
              onCheckedChange={(checked) => onRecommendedChange(checked === true)}
            />
            <Label className="cursor-pointer font-normal">{t("Show in the recommended rail")}</Label>
          </label>

          <ProductVisibilitySection
            visibility={entry.visibility}
            onChange={onVisibilityChange}
            open={visibilityOpen}
            onOpenChange={setVisibilityOpen}
          />
        </div>
      )}
    </div>
  );
};
